import type { ReactNode } from 'react'

/**
 * Typography for the public API reference pages (`/docs/api`, `/docs/workflows`).
 * Anchored sections and subsections, inline code, and a bordered table — the
 * pieces a hand-written reference page repeats on every screen.
 */

export function Section({ id, title, children }: { id: string; title: string; children: ReactNode }) {
  return (
    <section id={id} className="scroll-mt-24">
      <h2 className="group mb-4 text-lg font-semibold tracking-tight text-foreground">
        <a href={`#${id}`} className="inline-flex items-center gap-2">
          {title}
          <span
            aria-hidden
            className="text-sm text-muted-foreground/40 opacity-0 transition-opacity group-hover:opacity-100"
          >
            #
          </span>
        </a>
      </h2>
      <div className="space-y-4">{children}</div>
    </section>
  )
}

export function Subsection({ id, title, children }: { id?: string; title: string; children: ReactNode }) {
  return (
    <div id={id} className={id ? 'scroll-mt-24' : undefined}>
      <h3 className="mb-2 text-sm font-semibold text-foreground md:text-[15px]">{title}</h3>
      <div className="space-y-3">{children}</div>
    </div>
  )
}

export function InlineCode({ children }: { children: ReactNode }) {
  return (
    <code className="rounded bg-muted/60 px-1.5 py-0.5 font-mono text-[0.85em] text-foreground/90">{children}</code>
  )
}

export function DocsTable({ headers, rows }: { headers: string[]; rows: Array<Array<ReactNode>> }) {
  return (
    <div className="overflow-x-auto rounded-lg border border-border/60">
      <table className="w-full text-left text-xs md:text-sm">
        <thead>
          <tr className="border-b border-border/60 bg-muted/20">
            {headers.map(header => (
              <th key={header} className="px-3 py-2 font-medium text-foreground">
                {header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-border/40">
          {rows.map((row, rowIndex) => (
            <tr key={rowIndex}>
              {row.map((cell, cellIndex) => (
                <td key={cellIndex} className="px-3 py-2 align-top text-muted-foreground">
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
